import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxContent, Container, Content, StepsWrapper } from './styles';
import { CoreApiContext } from '../../../contexts/coreApiContext';
import { getLanguage, getAccountType } from '../../../services/auth';
import { Keys } from '../../../constants';
import { logError } from '../../../utils/logError';
import { getFromLocalStorage, removeFromLocalStorage } from '../../../utils/localStorage';
import { sendEventLog } from '../../../services/eventLog';
import { setDataLayer } from '../../../services/gtm';
import { transcodeLanguage } from '../../../utils/language';
import Footer from '../../../components/Footer';
import Header from '../../../components/Header';
import Loading from '../../../components/atoms/Loading';
import Notification from '../../../components/Notification';
import ProfilesCard from '../components/ProfilesCard';
import ContactCard from '../components/ContactCard';
import RoundedStepper from '../../../components/RoundedStepper';
import {
  updateAccountOnboardingStep,
  updateAccountOnboardingWhatsapp,
  updateAccountStartProfiles,
} from '../../../services/accountOnboarding';
import {
  buildEnhancedConversionHashes,
  toGoogleAdsUserData,
} from '../../../utils/enhancedConversionUserData';
import { countryCode, defaultCountryCode } from '../../../config/i18n';
import { getCountryCode } from '../../../utils/countryCodes';
import { isPhoneInvalid, phoneValidation } from '../../../utils/validations';

const STEP_BRANDS = 0;
const STEP_CONTACT = 1;

export default function Onboarding() {
  const { currentAccount } = useContext(CoreApiContext);
  const [profiles, setProfiles] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [canShowProfilesMenu, setCanShowProfilesMenu] = useState(false);
  const [notification, setNotification] = useState({});
  const [activeStep, setActiveStep] = useState(STEP_BRANDS);
  const [isFlagResolved, setIsFlagResolved] = useState(false);
  const [phone, setPhone] = useState(() => ({
    countryCode: countryCode[getLanguage()] || defaultCountryCode,
    number: '',
    phoneValidation: {},
  }));

  const { t } = useTranslation();
  const hasProfiles = !!profiles;
  const couponCode = getFromLocalStorage(Keys.COUPON_CODE) || '';
  const language = transcodeLanguage(getLanguage());
  const accountType = getAccountType() || 'email';
  const registerType = accountType === 'email' ? 'metodo_formulario' : 'metodo_facebook';
  const userPlanType = currentAccount?.quotas?.available ? 'subscriber' : 'trial';

  const steps = useMemo(
    () => [t('settings.steps.profiles.brands'), t('settings.steps.contact.label')],
    [t],
  );

  const profilesStepRef = useRef(false);
  const contactStepRef = useRef(false);

  const sendEventGtm = async ({ element, elementText, numberOfSelectedProfiles, userAction }) => {
    const hashes = await buildEnhancedConversionHashes({
      email: currentAccount?.email,
      userPhoneSHA256: currentAccount?.userPhoneSHA256,
    });

    setDataLayer({
      event: 'siteFireEvt.accountsCreationFlow.conversionEvent',
      eventProperty: {
        numberOfSelectedProfiles,
        element,
        elementText,
        userAction,
        primarySource: 'app',
        codeSource: 'front-->gtm-->endpoint',
        analiticalContext: 'comportamental',
        primaryContext: 'accounts',
        elementOriginalText: 'nao_aplicavel',
        appContext: 'cadastrar',
        appAction: 'finalizar_cadastro_step_perfis',
        socialNetwork: 'nao_aplicavel',
        postChannel: 'nao_aplicavel',
        numberOfDatesToPost: 0,
        totalPostScheduled: 0,
        appDetails: `${registerType} cadastro_realizado_novo_usuario`,
        siteLanguage: language,
        registerMethod: registerType,
        siteEventDetails: `cadastrar.finalizar_cadastro_step_perfis.${registerType}.cadastro_realizado_novo_usuario`,
      },
      userProperty: {
        userCostumerStatus: userPlanType,
        ...hashes,
      },
      user_data: toGoogleAdsUserData(hashes),
      transactionProperty: {
        transactionId: currentAccount?.id ? String(currentAccount.id) : '',
        accountNumber: currentAccount?.id,
      },
    });
  };

  const handleChatTrackingEvent = async ({ numberOfSelectedProfiles, email }) => {
    try {
      const hashes = await buildEnhancedConversionHashes({
        email,
        userPhoneSHA256: currentAccount?.userPhoneSHA256,
      });

      setDataLayer({
        event: 'siteFireEvt.accountsCreationFlow.conversionEvent',
        eventProperty: {
          codeSource: 'native-->dataLayer-->gtm-->endpoint',
          primarySource: 'chat_webapp',
          primaryContext: 'accounts',
          analiticalContext: 'comportamental',
          userAction: 'visitou',
          element: 'pagina',
          elementText: 'nao_aplicavel',
          elementOriginalText: 'nao_aplicavel',
          siteEventDetails:
            'cadastrar.finalizar_cadastro.metodo_formulario.cadastro_realizado_novo_usuario',
          socialNetwork: 'nao_aplicavel',
          postChannel: 'nao_aplicavel',
          appContext: 'cadastrar',
          appAction: 'finalizar_cadastro',
          appDetails: 'metodo_formulario.cadastro_realizado_novo_usuario',
          siteLanguage: language,
          registerMethod: 'from_chat-accounts.mlabs.io',
          numberOfSelectedProfiles,
          numberOfDatesToPost: 0,
          totalPostScheduled: 0,
          isFirstChatConnectedNetwork: false,
        },
        userProperty: {
          ...hashes,
          userCostumerStatus: userPlanType,
          ecommerce: {
            // eslint-disable-next-line camelcase
            social_media_name: '',
          },
        },
        user_data: toGoogleAdsUserData(hashes),
        transactionProperty: {
          transactionId: currentAccount?.id ? String(currentAccount.id) : '',
          accountNumber: currentAccount?.id,
        },
      });
    } catch (error) {
      logError('Error sending gtm tracking event');
    }
  };

  function accessRightStepProfiles(account) {
    const accessRights = account.access_rights;
    const access = 'sign_up_step_profiles';

    return access in accessRights ? accessRights[access] : true;
  }

  const postOnboardingStep = async (step) => {
    try {
      return await updateAccountOnboardingStep(currentAccount.id, step);
    } catch (error) {
      setIsLoading(false);
      setNotification({
        key: new Date().getTime(),
        message: error?.response?.data?.message || t('errors.connection-failure'),
      });
      if (step === 2) {
        contactStepRef.current = false;
      } else {
        profilesStepRef.current = false;
      }
    }
    return false;
  };

  const handleContactStep = async () => {
    setIsLoading(true);
    setActiveStep(STEP_CONTACT);

    if (contactStepRef.current) {
      setIsLoading(false);
      return;
    }

    contactStepRef.current = true;

    if (!currentAccount.onboarding_step || currentAccount.onboarding_step === 1) {
      await postOnboardingStep(2);
    }
    setIsLoading(false);
  };

  const handleProfilesStep = async () => {
    setIsLoading(true);

    try {
      sendEventLog('uso_onboarding_continuar');
      await updateAccountStartProfiles(currentAccount.id, {
        startProfilesCount: profiles,
        language,
        couponCode,
      });

      removeFromLocalStorage(Keys.ACCOUNT_CREATOR);
      removeFromLocalStorage(Keys.COUPON_CODE);

      if (process.env.REACT_APP_PRODUCT_KEY === 'chat') {
        await handleChatTrackingEvent({
          numberOfSelectedProfiles: profiles,
          email: currentAccount?.email,
        });
      } else {
        await sendEventGtm({
          numberOfSelectedProfiles: profiles,
          element: 'botao',
          elementText: 'Iniciar período de teste',
          userAction: 'clicou',
        });
      }

      try {
        await sendEventLog('signup_click_start-trial-period-button', {
          origin: currentAccount.origin_key,
        });
      } catch {
        logError(t('errors.send-event-log'));
      }

      await handleContactStep();
    } catch (error) {
      setIsLoading(false);
      setNotification({
        key: new Date().getTime(),
        message: error?.response?.data?.message || t('errors.profiles-save-failure'),
      });
    }
  };

  const handleFinish = async () => {
    setIsLoading(true);

    try {
      if (phone.number) {
        if (isPhoneInvalid(phone.number, phone.countryCode)) {
          setPhone((currentPhone) => ({
            ...currentPhone,
            phoneValidation: phoneValidation(currentPhone.number, currentPhone.countryCode),
          }));
          setIsLoading(false);
          return;
        }

        await updateAccountOnboardingWhatsapp(currentAccount.id, {
          phoneCode: getCountryCode(phone.countryCode),
          phoneNumber: phone.number,
        });
      }

      setDataLayer({
        event: 'onboarding_start_trial',
        eventProperty: {
          appAction: 'iniciar_teste_gratis',
          appContext: 'onboarding_contato',
        },
      });

      sendEventLog('uso_onboarding_IniciarTeste');
      window.location.assign(process.env.REACT_APP_FULL_URL);
    } catch (error) {
      setIsLoading(false);
      setNotification({
        key: new Date().getTime(),
        message: error?.response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  const handleSkipContact = () => {
    setDataLayer({
      event: 'onboarding_skip_contact',
      eventProperty: {
        appAction: 'pular_contato',
        appContext: 'onboarding_contato',
      },
    });

    sendEventLog('uso_onboarding_pular_contato');
    window.location.assign(process.env.REACT_APP_FULL_URL);
  };

  const isDisabledNextStep = useMemo(() => {
    return activeStep === STEP_BRANDS && !hasProfiles;
  }, [hasProfiles, activeStep]);

  const handleContinue = async () => {
    if (activeStep === STEP_CONTACT) {
      await handleFinish();
      return;
    }

    handleProfilesStep();
  };

  const handleRedirectAccountPartner = async () => {
    await updateAccountStartProfiles(currentAccount.id, {
      startProfilesCount: 0,
      language,
      couponCode: '',
    });

    window.location.assign(process.env.REACT_APP_FULL_URL);
  };

  const handleBackButton = () => {
    sendEventLog('uso_onboarding_voltar');
    setActiveStep(STEP_BRANDS);
  };

  const handleGtmTrackingEvent = async () => {
    if (process.env.REACT_APP_PRODUCT_KEY === 'chat') {
      await handleChatTrackingEvent({ numberOfSelectedProfiles: 0, email: currentAccount?.email });
      return;
    }

    await sendEventGtm({
      numberOfSelectedProfiles: 0,
      userAction: 'visitou',
      element: 'pagina',
      elementText: 'nao_aplicavel',
    });
  };

  useEffect(() => {
    setIsFlagResolved(true);
  }, []);

  useEffect(() => {
    if (
      isFlagResolved &&
      currentAccount?.id &&
      currentAccount.onboarding_step &&
      currentAccount.onboarding_step >= 3
    ) {
      window.location.assign(process.env.REACT_APP_FULL_URL);
    }
  }, [isFlagResolved, currentAccount]);

  useEffect(() => {
    if (currentAccount?.id) {
      handleGtmTrackingEvent();

      if (!accessRightStepProfiles(currentAccount)) {
        handleRedirectAccountPartner();
      } else {
        setCanShowProfilesMenu(true);
      }

      if (!profiles && currentAccount.onboarding_step && currentAccount.onboarding_step > 0) {
        setActiveStep(currentAccount.onboarding_step - 1);
        setProfiles(currentAccount.start_profiles_count);
      }
    }
  }, [currentAccount]);

  useEffect(() => {
    if (
      activeStep === STEP_BRANDS &&
      profiles &&
      !profilesStepRef.current &&
      !currentAccount.onboarding_step
    ) {
      profilesStepRef.current = true;
      postOnboardingStep(1);
    }
  }, [activeStep, profiles]);

  return (
    <Container>
      {(!canShowProfilesMenu || isLoading || !isFlagResolved) && <Loading opacity />}
      {canShowProfilesMenu && isFlagResolved && (
        <>
          <Header showLogo />
          <Content>
            <Box
              $maxWidth={activeStep === STEP_BRANDS ? '729px' : '580px'}
              $allowOverflow={activeStep === STEP_CONTACT}
            >
              <StepsWrapper>
                <RoundedStepper steps={steps} activeStep={activeStep} />
              </StepsWrapper>
              <BoxContent $allowOverflow={activeStep === STEP_CONTACT}>
                {activeStep === STEP_BRANDS ? (
                  <ProfilesCard profiles={profiles} setProfiles={setProfiles} />
                ) : (
                  <ContactCard phone={phone} onPhoneChange={setPhone} />
                )}
              </BoxContent>
            </Box>
          </Content>
          <Footer
            activeStep={activeStep}
            listStep={[STEP_BRANDS, STEP_CONTACT]}
            onGoNext={handleContinue}
            onGoBack={activeStep === STEP_CONTACT ? handleBackButton : undefined}
            onSkip={activeStep === STEP_CONTACT ? handleSkipContact : undefined}
            disabled={isDisabledNextStep}
            hasMarginTop={false}
            isLastStep={activeStep === STEP_CONTACT}
            maxWidth={activeStep === STEP_CONTACT ? '580px' : undefined}
          />
          {!!notification?.key && (
            <Notification index={notification.key} message={notification.message} />
          )}
        </>
      )}
    </Container>
  );
}
